<template>
    <div class="level-content">
        <div class="top">
            <div class="title">数学水平评估</div>
            <div class="collapse-btn" @click="handleClose">
                <div class="el-tooltip fold-area">
                    <i class="el-icon-circle-close close-btn"></i>
                </div>
            </div>
        </div>

        <div class="level-cards">
            <!-- 初中数学水平卡片 -->
            <div class="level-card junior-card" :class="{ 'loading': loadingJunior }">
                <div class="card-icon">
                    <i class="el-icon-school"></i>
                </div>
                <div class="card-title">初中数学水平</div>
                <div class="card-score" v-if="!loadingJunior">
                    <span class="score-value">{{ juniorScore || '--' }}</span>
                    <span class="score-unit">分</span>
                </div>
                <div class="card-loading" v-else>
                    <i class="el-icon-loading"></i> 加载中...
                </div>
                <div class="card-level" v-if="juniorLevel">
                    <span class="level-badge" :class="getLevelClass(juniorLevel)">{{ juniorLevel }}</span>
                </div>
            </div>

            <!-- 高中数学水平卡片 -->
            <div class="level-card senior-card" :class="{ 'loading': loadingSenior }">
                <div class="card-icon">
                    <i class="el-icon-reading"></i>
                </div>
                <div class="card-title">高中数学水平</div>
                <div class="card-score" v-if="!loadingSenior">
                    <span class="score-value">{{ seniorScore || '--' }}</span>
                    <span class="score-unit">分</span>
                </div>
                <div class="card-loading" v-else>
                    <i class="el-icon-loading"></i> 加载中...
                </div>
                <div class="card-level" v-if="seniorLevel">
                    <span class="level-badge" :class="getLevelClass(seniorLevel)">{{ seniorLevel }}</span>
                </div>
            </div>
        </div>

        <!-- 底部提示 -->
        <div class="level-footer">
            <span>根据近期学习情况评估，持续练习可提升水平</span>
        </div>
    </div>
</template>

<script>
import { getMathLevel } from "@/api/chat/sendReceive";

export default {
    data() {
        return {
            juniorScore: "",
            seniorScore: "",
            juniorLevel: "",
            seniorLevel: "",
            loadingJunior: false,
            loadingSenior: false
        }
    },
    mounted() {
        this.getScores()
    },
    activated() {          // ← 添加这个钩子
        this.getScores();  // 确保每次显示页面都刷新数据
    },
    methods: {
        handleClose() {
            this.$router.go(-1)
        },
        async getScores() {
            await Promise.all([
                this.getJuniorScore(),
                this.getSeniorScore()
            ]);
        },
        async getJuniorScore() {
            this.loadingJunior = true;
            try {
                // 请根据实际后端接口调整
                let { data: res } = await getMathLevel({ level: 'm' });
                if (res && res.mathlevel) {
                    this.juniorScore = res.mathlevel;
                    this.juniorLevel = this.getLevelText(res.mathlevel);
                } else {
                    this.juniorScore = "暂未评估";
                }
            } catch (error) {
                console.error("获取初中数学水平失败:", error);
                this.juniorScore = "获取失败";
            } finally {
                this.loadingJunior = false;
            }
        },
        async getSeniorScore() {
            this.loadingSenior = true;
            try {
                let { data: res } = await getMathLevel({ level: 'h' });
                if (res && res.mathlevel) {
                    this.seniorScore = res.mathlevel;
                    this.seniorLevel = this.getLevelText(res.mathlevel);
                } else {
                    this.seniorScore = "暂未评估";
                }
            } catch (error) {
                console.error("获取高中数学水平失败:", error);
                this.seniorScore = "获取失败";
            } finally {
                this.loadingSenior = false;
            }
        },
        getLevelText(score) {
            if (!score) return "待评估";
            const numScore = parseInt(score);
            if (numScore >= 90) return "优秀";
            if (numScore >= 75) return "良好";
            if (numScore >= 60) return "合格";
            return "提升中";
        },
        getLevelClass(level) {
            const classMap = {
                "优秀": "level-excellent",
                "良好": "level-good",
                "合格": "level-pass",
                "提升中": "level-improving",
                "待评估": "level-pending"
            };
            return classMap[level] || "level-pending";
        }
    }
}
</script>

<style lang="scss" scoped>
/* 样式与之前美化版本相同，此处省略重复代码，请从上一轮答案中复制完整样式 */
.level-content {
    height: 100vh; /* 降级方案 */
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)); /* 合并底部安全区 */
    position: relative;
    font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Microsoft YaHei', sans-serif;
    overflow-y: auto; /* 启用垂直滚动 */
    -webkit-overflow-scrolling: touch; /* 流畅滚动 */
    box-sizing: border-box;

    /* 使用动态视口高度（现代浏览器） */
    @supports (height: 100dvh) {
        height: 100dvh;
    }

    .top {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        margin-bottom: 30px;
        padding-top: 20px;

        .title {
            font-size: 24px;
            font-weight: 600;
            color: #fff;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
            letter-spacing: 1px;
        }

        .collapse-btn {
            position: absolute;
            right: 0;
            top: 20px;
            cursor: pointer;
            
            .fold-area {
                width: 36px;
                height: 36px;
                background: rgba(255,255,255,0.2);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;
                
                &:hover {
                    background: rgba(255,255,255,0.3);
                    transform: rotate(90deg);
                }
                
                .close-btn {
                    font-size: 20px;
                    color: #fff;
                    font-weight: 600;
                }
            }
        }
    }

    .level-cards {
        display: flex;
        flex-direction: column;
        gap: 20px;
        max-width: 400px;
        margin: 0 auto;
        padding: 10px;
        padding-bottom: 10px; /* 增加底部留白 */

        .level-card {
            background: rgba(255,255,255,0.95);
            border-radius: 20px;
            padding: 25px 20px;
            text-align: center;
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
            cursor: default;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            
            &:hover {
                transform: translateY(-5px);
                box-shadow: 0 15px 40px rgba(0,0,0,0.15);
            }
            
            &.loading {
                opacity: 0.7;
            }
            
            .card-icon {
                font-size: 48px;
                margin-bottom: 15px;
                
                i {
                    color: #667eea;
                }
            }
            
            .card-title {
                font-size: 18px;
                font-weight: 600;
                color: #333;
                margin-bottom: 15px;
                letter-spacing: 1px;
            }
            
            .card-score {
                margin-bottom: 15px;
                
                .score-value {
                    font-size: 48px;
                    font-weight: bold;
                    color: #667eea;
                    line-height: 1;
                }
                
                .score-unit {
                    font-size: 16px;
                    color: #999;
                    margin-left: 5px;
                }
            }
            
            .card-loading {
                color: #999;
                font-size: 14px;
                margin-bottom: 15px;
                
                i {
                    margin-right: 5px;
                }
            }
            
            .card-level {
                .level-badge {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 500;
                    
                    &.level-excellent {
                        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                        color: #fff;
                    }
                    
                    &.level-good {
                        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                        color: #fff;
                    }
                    
                    &.level-pass {
                        background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
                        color: #fff;
                    }
                    
                    &.level-improving {
                        background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
                        color: #fff;
                    }
                    
                    &.level-pending {
                        background: #e0e0e0;
                        color: #999;
                    }
                }
            }
        }
        
        .junior-card {
            .card-icon i {
                color: #4facfe;
            }
            
            .score-value {
                color: #4facfe;
            }
        }
        
        .senior-card {
            .card-icon i {
                color: #f093fb;
            }
            
            .score-value {
                color: #f093fb;
            }
        }
    }
    
    .level-footer {
        text-align: center;
        margin-top: 30px;
        padding: 15px;
        
        span {
            font-size: 12px;
            color: rgba(255,255,255,0.8);
            background: rgba(0,0,0,0.2);
            padding: 8px 16px;
            border-radius: 20px;
            display: inline-block;
        }
    }
}

@media (max-width: 768px) {
    .level-content {
        padding: 15px;
        
        .top .title {
            font-size: 20px;
        }
        
        .level-cards {
            gap: 15px;
            
            .level-card {
                padding: 20px 15px;
                
                .card-icon {
                    font-size: 40px;
                }
                
                .card-title {
                    font-size: 16px;
                }
                
                .card-score .score-value {
                    font-size: 40px;
                }
            }
        }
    }
}
</style>